import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'
import NotificationsBell from '../components/NotificationsBell'

interface User { id: string; email: string; username: string; role: string; created_at: string }
interface Stats { videosCount: number; likesCount: number; commentsCount: number; requestsCount: number }
interface Props { currentUser: User | null }

const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })

const Counter: React.FC<{ value: number }> = ({ value }) => {
  const [display, setDisplay] = useState(0)
  const raf = useRef<number | null>(null)
  useEffect(() => {
    const start = performance.now()
    const step = (now: number) => {
      const pct = Math.min((now - start) / 800, 1)
      const ease = 1 - Math.pow(1 - pct, 3)
      setDisplay(Math.round(ease * value))
      if (pct < 1) raf.current = requestAnimationFrame(step)
    }
    raf.current = requestAnimationFrame(step)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [value])
  return <>{display >= 1000 ? `${(display / 1000).toFixed(1)}K` : display}</>
}

interface MenuRowProps {
  icon: string; grad: string; title: string; sub: string
  badge?: string; badgeColor?: string
  onClick?: () => void; isDark: boolean
  cardBg: string; border: string; textColor: string; subColor: string
}
const MenuRow: React.FC<MenuRowProps> = ({ icon, grad, title, sub, badge, badgeColor, onClick, isDark, cardBg, border, textColor, subColor }) => {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 16,
        padding: '16px 20px',
        background: hovered ? (isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.04)') : cardBg,
        border: `1px solid ${border}`,
        borderRadius: 18, cursor: onClick ? 'pointer' : 'default',
        textAlign: 'left', transition: 'all .2s',
        transform: hovered ? 'translateX(3px)' : 'translateX(0)',
        boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,.06)',
      }}
    >
      <div style={{ width: 46, height: 46, borderRadius: 23, background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,.2)' }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <p style={{ color: textColor, fontSize: 15, fontWeight: 600, margin: 0 }}>{title}</p>
          {badge && badgeColor && (
            <span style={{ padding: '1px 8px', borderRadius: 8, background: `${badgeColor}22`, border: `1px solid ${badgeColor}44`, color: badgeColor, fontSize: 9, fontWeight: 800, letterSpacing: '.5px' }}>{badge}</span>
          )}
        </div>
        <p style={{ color: subColor, fontSize: 12, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</p>
      </div>
      {onClick && <span style={{ color: subColor, fontSize: 20, flexShrink: 0 }}>›</span>}
    </button>
  )
}

const ProfileScreen: React.FC<Props> = ({ currentUser }) => {
  const navigate = useNavigate()
  const { isDark, t, i18n, toggleTheme } = useTheme()
  const [profile, setProfile]           = useState<User | null>(null)
  const [stats, setStats]               = useState<Stats>({ videosCount: 0, likesCount: 0, commentsCount: 0, requestsCount: 0 })
  const [loading, setLoading]           = useState(true)
  const [activeModal, setActiveModal]   = useState<'edit' | 'request' | 'password' | null>(null)
  const [editUsername, setEditUsername] = useState('')
  const [editSaving, setEditSaving]     = useState(false)
  const [reqTitle, setReqTitle]         = useState('')
  const [reqDesc, setReqDesc]           = useState('')
  const [reqType, setReqType]           = useState('movie')
  const [reqSaving, setReqSaving]       = useState(false)
  const [newPass, setNewPass]           = useState('')
  const [confirmPass, setConfirmPass]   = useState('')
  const [passSaving, setPassSaving]     = useState(false)
  const [showPass1, setShowPass1]       = useState(false)
  const [showPass2, setShowPass2]       = useState(false)

  const isAdmin = profile?.role === 'admin'

  const C = {
    bg:       isDark ? '#080810' : '#f0f4f8',
    card:     isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    border:   isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    text:     isDark ? '#ffffff' : '#0f172a',
    sub:      isDark ? '#666'    : '#64748b',
    muted:    isDark ? '#2a2a3a' : '#cbd5e1',
    inputBg:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    accent:   isAdmin ? '#a855f7' : '#e53935',
    heroGrad: isAdmin
      ? 'linear-gradient(135deg,#1a0533 0%,#2d1b69 50%,#08080f 100%)'
      : 'linear-gradient(135deg,#1a0505 0%,#4a1010 50%,#08080f 100%)',
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '13px 16px',
    background: C.inputBg, border: `1px solid ${C.border}`,
    borderRadius: 12, color: C.text, fontSize: 15,
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  }

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return }
    fetchProfile()
  }, [currentUser])

  const fetchProfile = async () => {
    if (!currentUser) return
    const { data } = await supabase.from('profiles').select('*').eq('id', currentUser.id).single()
    if (data) setProfile(data)
    const [v, l, c, r] = await Promise.all([
      supabase.from('videos').select('*', { count: 'exact', head: true }).eq('created_by', currentUser.id),
      supabase.from('likes').select('*', { count: 'exact', head: true }).eq('user_id', currentUser.id),
      supabase.from('comments').select('*', { count: 'exact', head: true }).eq('user_id', currentUser.id),
      supabase.from('movie_requests').select('*', { count: 'exact', head: true }).eq('requested_by', currentUser.id),
    ])
    setStats({ videosCount: v.count || 0, likesCount: l.count || 0, commentsCount: c.count || 0, requestsCount: r.count || 0 })
    setLoading(false)
  }

  const handleLogout = async () => {
    if (window.confirm(t('logoutConfirm'))) { await supabase.auth.signOut(); navigate('/') }
  }

  const handleSaveUsername = async () => {
    if (!editUsername.trim() || editUsername.length < 3) { alert('Username must be at least 3 characters'); return }
    setEditSaving(true)
    const { error } = await supabase.from('profiles').update({ username: editUsername.trim() }).eq('id', currentUser!.id)
    if (!error) { setProfile(p => p ? { ...p, username: editUsername.trim() } : p); setActiveModal(null) }
    else alert(error.message)
    setEditSaving(false)
  }

  const handleSubmitRequest = async () => {
    if (!reqTitle.trim()) { alert('Title is required'); return }
    setReqSaving(true)
    const { error } = await supabase.from('movie_requests').insert([{
      title: reqTitle.trim(), description: reqDesc.trim(), content_type: reqType,
      requested_by: currentUser!.id, status: 'pending',
    }])
    if (!error) { setReqTitle(''); setReqDesc(''); setReqType('movie'); setActiveModal(null); fetchProfile() }
    else alert(error.message)
    setReqSaving(false)
  }

  const handleChangePassword = async () => {
    if (!newPass || newPass.length < 6) { alert('Password must be at least 6 characters'); return }
    if (newPass !== confirmPass) { alert('Passwords do not match'); return }
    setPassSaving(true)
    const { error } = await supabase.auth.updateUser({ password: newPass })
    if (!error) { setNewPass(''); setConfirmPass(''); setActiveModal(null); alert('Password changed!') }
    else alert(error.message)
    setPassSaving(false)
  }

  const Modal: React.FC<{ title: string; accent: string; onClose: () => void; children: React.ReactNode }> = ({ title, accent, onClose, children }) => (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20, backdropFilter: 'blur(8px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: isDark ? '#0f1018' : '#fff', borderRadius: 24, maxWidth: 460, width: '100%', overflow: 'hidden', border: `1px solid ${C.border}`, animation: 'modalIn .25s ease', boxShadow: '0 32px 80px rgba(0,0,0,.6)' }}>
        <div style={{ height: 3, background: accent }} />
        <div style={{ padding: '28px 28px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h3 style={{ color: C.text, fontSize: 20, fontWeight: 800, margin: 0 }}>{title}</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>
          {children}
        </div>
      </div>
    </div>
  )

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${C.accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )

  const initial = profile?.username?.charAt(0).toUpperCase() || '?'

  return (
    <>
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg) } }
        @keyframes modalIn { from { opacity:0;transform:scale(.96) translateY(8px) } to { opacity:1;transform:scale(1) translateY(0) } }
        @keyframes fadeUp  { from { opacity:0;transform:translateY(16px) } to { opacity:1;transform:translateY(0) } }
        @keyframes pulse   { 0%,100%{opacity:.7} 50%{opacity:1} }
        @keyframes glow    { 0%,100%{box-shadow:0 0 8px #22c55e66} 50%{box-shadow:0 0 16px #22c55eaa} }
      `}</style>

      {/* ── MODALS ── */}
      {activeModal === 'edit' && (
        <Modal title="✏️ Edit Username" accent={`linear-gradient(90deg,${C.accent},#8e24aa)`} onClose={() => setActiveModal(null)}>
          <p style={{ color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>Username</p>
          <input style={{ ...inp, marginBottom: 24 }} value={editUsername} onChange={e => setEditUsername(e.target.value)} placeholder="Enter new username" maxLength={20} autoFocus
            onFocus={() => { if (!editUsername) setEditUsername(profile?.username || '') }} />
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setActiveModal(null)} style={{ flex: 1, padding: '13px 0', borderRadius: 12, border: `1px solid ${C.border}`, background: 'transparent', color: C.sub, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSaveUsername} disabled={editSaving} style={{ flex: 2, padding: '13px 0', borderRadius: 12, border: 'none', background: `linear-gradient(90deg,${C.accent},#8e24aa)`, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: editSaving ? .7 : 1 }}>
              {editSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {activeModal === 'request' && (
        <Modal title="🎬 Request a Video" accent="linear-gradient(90deg,#e53935,#8e24aa)" onClose={() => setActiveModal(null)}>
          <p style={{ color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>Title *</p>
          <input style={{ ...inp, marginBottom: 16 }} value={reqTitle} onChange={e => setReqTitle(e.target.value)} placeholder="Movie / Series name…" autoFocus />
          <p style={{ color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>Notes</p>
          <textarea style={{ ...inp, minHeight: 80, resize: 'none', marginBottom: 16 }} value={reqDesc} onChange={e => setReqDesc(e.target.value)} placeholder="Any extra details…" />
          <p style={{ color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 10 }}>Type</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            {['movie', 'series', 'short_drama', 'podcast'].map(tp => (
              <button key={tp} onClick={() => setReqType(tp)} style={{ padding: '7px 14px', borderRadius: 20, border: `1px solid ${reqType === tp ? '#e53935' : C.border}`, background: reqType === tp ? 'rgba(229,57,53,.15)' : 'transparent', color: reqType === tp ? '#e53935' : C.sub, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all .15s' }}>
                {tp.replace('_', ' ')}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setActiveModal(null)} style={{ flex: 1, padding: '13px 0', borderRadius: 12, border: `1px solid ${C.border}`, background: 'transparent', color: C.sub, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSubmitRequest} disabled={reqSaving} style={{ flex: 2, padding: '13px 0', borderRadius: 12, border: 'none', background: 'linear-gradient(90deg,#e53935,#8e24aa)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: reqSaving ? .7 : 1 }}>
              {reqSaving ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </Modal>
      )}

      {activeModal === 'password' && (
        <Modal title="🔑 Change Password" accent="linear-gradient(90deg,#3b82f6,#8e24aa)" onClose={() => setActiveModal(null)}>
          {[
            { label: 'New Password',     val: newPass,     set: setNewPass,     show: showPass1, toggle: () => setShowPass1(v => !v) },
            { label: 'Confirm Password', val: confirmPass, set: setConfirmPass, show: showPass2, toggle: () => setShowPass2(v => !v) },
          ].map((f, i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <p style={{ color: C.sub, fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>{f.label}</p>
              <div style={{ position: 'relative' }}>
                <input type={f.show ? 'text' : 'password'} style={{ ...inp, paddingRight: 48 }} value={f.val} onChange={e => f.set(e.target.value)} placeholder="Min. 6 characters" />
                <button onClick={f.toggle} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 16, padding: 0 }}>
                  {f.show ? '🙈' : '👁'}
                </button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button onClick={() => setActiveModal(null)} style={{ flex: 1, padding: '13px 0', borderRadius: 12, border: `1px solid ${C.border}`, background: 'transparent', color: C.sub, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleChangePassword} disabled={passSaving} style={{ flex: 2, padding: '13px 0', borderRadius: 12, border: 'none', background: 'linear-gradient(90deg,#3b82f6,#8e24aa)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: passSaving ? .7 : 1 }}>
              {passSaving ? 'Saving…' : 'Change Password'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── PAGE ── */}
      <div style={{ minHeight: '100vh', background: C.bg }}>

        {/* ── HERO ── */}
        <div style={{ background: C.heroGrad, position: 'relative', overflow: 'hidden', paddingBottom: 64 }}>
          <div style={{ position: 'absolute', top: -80, left: -80, width: 320, height: 320, borderRadius: '50%', background: `radial-gradient(circle,${C.accent}28 0%,transparent 70%)`, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -40, right: -60, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle,rgba(59,130,246,.14) 0%,transparent 70%)', pointerEvents: 'none' }} />

          {/* Top bar */}
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '18px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 20, padding: '7px 16px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(10px)' }}>
              ← Home
            </button>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {currentUser && <NotificationsBell userId={currentUser.id} />}
              <button onClick={toggleTheme} style={{ width: 38, height: 38, borderRadius: 19, background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)' }}>
                {isDark ? '☀️' : '🌙'}
              </button>
              <button onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'sw' : 'en')} style={{ padding: '7px 14px', background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 18, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(10px)' }}>
                {i18n.language === 'en' ? '🇹🇿 SW' : '🇬🇧 EN'}
              </button>
            </div>
          </div>

          {/* Avatar section */}
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '36px 24px 0', textAlign: 'center', animation: 'fadeUp .5s ease' }}>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 20 }}>
              {/* Outer glow ring */}
              <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', background: `conic-gradient(from 0deg, ${C.accent}, #8e24aa, #3b82f6, ${C.accent})`, opacity: .5, animation: 'spin 4s linear infinite' }} />
              <div style={{ position: 'relative', width: 100, height: 100, borderRadius: 50, background: isAdmin ? 'linear-gradient(135deg,#ab47bc,#7b1fa2)' : 'linear-gradient(135deg,#ef5350,#c62828)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 42, fontWeight: 900, color: '#fff', border: '4px solid #08080f', boxShadow: `0 16px 48px rgba(0,0,0,.5)` }}>
                {initial}
              </div>
              <div style={{ position: 'absolute', bottom: 4, right: 4, width: 18, height: 18, borderRadius: 9, background: '#22c55e', border: '3px solid #08080f', animation: 'glow 2s ease infinite' }} />
            </div>

            <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 900, margin: '0 0 6px', letterSpacing: '-.3px' }}>{profile?.username}</h1>
            <p style={{ color: 'rgba(255,255,255,.5)', fontSize: 14, margin: '0 0 14px' }}>{profile?.email}</p>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 18px', borderRadius: 24, background: isAdmin ? 'rgba(168,85,247,.18)' : 'rgba(229,57,53,.18)', border: `1px solid ${isAdmin ? 'rgba(168,85,247,.35)' : 'rgba(229,57,53,.35)'}`, marginBottom: 10, backdropFilter: 'blur(10px)' }}>
              <div style={{ width: 6, height: 6, borderRadius: 3, background: C.accent, animation: 'pulse 2s ease infinite' }} />
              <span style={{ color: '#fff', fontSize: 12, fontWeight: 800, letterSpacing: '.5px' }}>{isAdmin ? '👑 ADMIN' : '👤 MEMBER'}</span>
            </div>

            <p style={{ color: 'rgba(255,255,255,.32)', fontSize: 12, margin: '8px 0 24px' }}>
              Member since {profile?.created_at ? fmtDate(profile.created_at) : '—'}
            </p>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => { setEditUsername(profile?.username || ''); setActiveModal('edit') }}
                style={{ padding: '9px 22px', background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', borderRadius: 22, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(10px)', transition: 'all .2s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,.12)'}
              >
                ✏️ Edit Profile
              </button>
              <button
                onClick={async () => { try { await navigator.share({ title: 'VidyShort', text: 'Check out VidyShort!', url: window.location.origin }) } catch { /* share not supported */ } }}
                style={{ padding: '9px 22px', background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 22, color: 'rgba(255,255,255,.8)', fontSize: 13, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(10px)', transition: 'all .2s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,.07)'}
              >
                📤 Share
              </button>
            </div>
          </div>
        </div>

        {/* ── STATS ── */}
        <div style={{ maxWidth: 900, margin: '-30px auto 0', padding: '0 24px', position: 'relative', zIndex: 10, animation: 'fadeUp .5s ease .1s both' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            {[
              { label: 'Videos',   value: stats.videosCount,   icon: '🎬', grad: 'linear-gradient(135deg,#e53935,#9333ea)' },
              { label: 'Liked',    value: stats.likesCount,    icon: '❤️',  grad: 'linear-gradient(135deg,#a855f7,#ec4899)' },
              { label: 'Comments', value: stats.commentsCount, icon: '💬', grad: 'linear-gradient(135deg,#3b82f6,#06b6d4)' },
              { label: 'Requests', value: stats.requestsCount, icon: '📋', grad: 'linear-gradient(135deg,#f97316,#ef4444)' },
            ].map(s => (
              <div key={s.label} style={{ borderRadius: 18, overflow: 'hidden', boxShadow: '0 8px 28px rgba(0,0,0,.3)', transition: 'transform .25s, box-shadow .25s', cursor: 'default' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,.4)' }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,.3)' }}
              >
                <div style={{ padding: '20px 14px 18px', background: s.grad, textAlign: 'center', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: -8, right: -8, fontSize: 44, opacity: .1 }}>{s.icon}</div>
                  <p style={{ color: '#fff', fontSize: 26, fontWeight: 900, margin: '0 0 4px' }}>
                    <Counter value={s.value} />
                  </p>
                  <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 11, fontWeight: 700, margin: 0, letterSpacing: '.5px', textTransform: 'uppercase' }}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MENU ── */}
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 60px', animation: 'fadeUp .5s ease .2s both' }}>

          <SectionLabel text={t('account')} sub={C.sub} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
            {isAdmin ? (
              <MenuRow icon="⚡" grad="linear-gradient(135deg,#8e24aa,#4a148c)" title="Admin Console" sub="Manage videos, users and analytics" badge="ADMIN" badgeColor="#a855f7" onClick={() => navigate('/admin')} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
            ) : (
              <MenuRow icon="🎬" grad="linear-gradient(135deg,#e53935,#9333ea)" title="Request a Video" sub="Ask for your favourite movie or series" onClick={() => setActiveModal('request')} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
            )}
            <MenuRow icon="🔔" grad="linear-gradient(135deg,#e53935,#8e24aa)" title="Notifications" sub="Mentions, replies and activity" onClick={() => navigate('/notifications')} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
            <MenuRow icon="🔑" grad="linear-gradient(135deg,#3b82f6,#06b6d4)" title="Change Password" sub="Update your account password" onClick={() => setActiveModal('password')} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
            <MenuRow icon={isDark ? '☀️' : '🌙'} grad={isDark ? 'linear-gradient(135deg,#f59e0b,#ef4444)' : 'linear-gradient(135deg,#1e40af,#7c3aed)'} title={isDark ? 'Light Mode' : 'Dark Mode'} sub={isDark ? 'Switch to light theme' : 'Switch to dark theme'} onClick={toggleTheme} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
            <MenuRow icon="🌐" grad="linear-gradient(135deg,#10b981,#059669)" title="Language" sub={i18n.language === 'en' ? 'English — click to switch to Swahili' : 'Kiswahili — bonyeza kubadilisha'} onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'sw' : 'en')} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
          </div>

          <SectionLabel text={t('support')} sub={C.sub} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
            <MenuRow icon="📞" grad="linear-gradient(135deg,#10b981,#059669)" title="Contact Support" sub="support@vidyshort.co.tz" onClick={() => window.open('mailto:support@vidyshort.co.tz')} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
            <MenuRow icon="📝" grad="linear-gradient(135deg,#f59e0b,#d97706)" title="Terms & Privacy" sub="View our terms of service and privacy policy" onClick={() => window.open('https://vidyshort.co.tz/terms')} isDark={isDark} cardBg={C.card} border={C.border} textColor={C.text} subColor={C.sub} />
          </div>

          <SectionLabel text="Danger Zone" sub={C.sub} />
          <button
            onClick={handleLogout}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: 'rgba(229,57,53,.06)', border: '1px solid rgba(229,57,53,.18)', borderRadius: 18, cursor: 'pointer', textAlign: 'left', transition: 'all .2s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(229,57,53,.12)'; e.currentTarget.style.borderColor = 'rgba(229,57,53,.36)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(229,57,53,.06)'; e.currentTarget.style.borderColor = 'rgba(229,57,53,.18)' }}
          >
            <div style={{ width: 46, height: 46, borderRadius: 23, background: 'linear-gradient(135deg,#e53935,#b71c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, boxShadow: '0 4px 12px rgba(229,57,53,.3)' }}>🚪</div>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#e53935', fontSize: 15, fontWeight: 700, margin: 0 }}>{t('logout')}</p>
              <p style={{ color: C.sub, fontSize: 12, margin: '2px 0 0' }}>Sign out of your account</p>
            </div>
            <span style={{ color: 'rgba(229,57,53,.45)', fontSize: 20 }}>›</span>
          </button>

          <div style={{ textAlign: 'center', marginTop: 40, paddingTop: 24, borderTop: `1px solid ${C.border}` }}>
            <p style={{ color: C.sub, fontSize: 12, margin: 0, opacity: .6 }}>VidyShort v1.0.0 · Built with ❤️ in Tanzania</p>
          </div>
        </div>
      </div>
    </>
  )
}

const SectionLabel: React.FC<{ text: string; sub: string }> = ({ text, sub }) => (
  <p style={{ color: sub, fontSize: 11, fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 14 }}>{text}</p>
)

export default ProfileScreen